import { StackItem, FormGroup, Level, LevelItem, Dropdown, KebabToggle, DropdownItem, Modal, ModalVariant, Button, DropdownPosition, InputGroup, DropdownToggle, Divider, Stack, Checkbox, Popover, Split, SplitItem } from '@patternfly/react-core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ExclamationCircleIcon, CogIcon, OutlinedQuestionCircleIcon } from '@patternfly/react-icons';
import { changeDicomAttributeRuleAction, removeDicomAttributeRule, StoreState } from '../../store/reducers';
import { selectDicomAttributeRuleById } from '../../store/selectors';
import DebouncedTextInput from '../../components/debounced-text-input';
import { ModelSelectionUndoState } from './SelectionRulePage';
import { literalTextToRegex, regexToLiteralText } from '../../util/string-convert';
import { ModelSelectionScope } from '../../store/model-customization-types';
import { useTranslation } from 'react-i18next';


interface ModelSelectionRuleDicomAttributeProps {
    dicomAttributeRuleId: string,
    displayId: number,
    setUndoState: (undoState: ModelSelectionUndoState) => void,
    onRemoveDicomRule: () => void,
    disableUndo?: boolean,
}

const ModelSelectionRuleDicomAttribute = (props: ModelSelectionRuleDicomAttributeProps) => {
    const { dicomAttributeRuleId, displayId, setUndoState, onRemoveDicomRule, disableUndo } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [isActionListOpen, setActionListOpenState] = useState(false);
    const [isRemoveAttributeModalOpen, setRemoveAttributeModalOpenState] = useState(false);
    // const [isAttributeHovered, setAttributeHovered] = useState(false);
    const [isPresetToggleOpen, setIsPresetToggleOpen] = useState(false);
    const [isRegexModeOn, setRegexModeOn] = useState(false);
    const [plainTextValue, setPlainTextValue] = useState<string | null>(null);

    const dicomAttributeRule = useSelector((state: StoreState) => selectDicomAttributeRuleById(state, dicomAttributeRuleId));

    useEffect(() => {
        const plainText = dicomAttributeRule !== undefined ? regexToLiteralText(dicomAttributeRule.value) : null;
        setPlainTextValue(plainText);
        if (plainText === null) { setRegexModeOn(true); }
    }, [dicomAttributeRule]);

    const handleRemoveAttributeButtonClick = useCallback(() => {
        setActionListOpenState(false);
        setRemoveAttributeModalOpenState(true);
    }, []);

    const handleRemoveAttributeConfirmation = useCallback(() => {
        setRemoveAttributeModalOpenState(false);
        dispatch(removeDicomAttributeRule(dicomAttributeRuleId));
    }, [dispatch, dicomAttributeRuleId]);

    // const handleRemoveAttributeHover = useCallback(() => {
    //     setAttributeHovered(true);
    // }, []);

    // const handleRemoveAttributeHoverLeave = useCallback(() => {
    //     setAttributeHovered(false);
    // }, []);

    const dispatchDicomAttributeNameChange = useCallback((name: string) => {
        if (dicomAttributeRule) {
            dispatch(changeDicomAttributeRuleAction(dicomAttributeRuleId, name, dicomAttributeRule.value));
        }
    }, [dispatch, dicomAttributeRule, dicomAttributeRuleId]);

    const dispatchDicomAttributeValueChange = useCallback((value: string) => {
        if (dicomAttributeRule) {
            const regexValue = isRegexModeOn ? value : literalTextToRegex(value);
            dispatch(changeDicomAttributeRuleAction(dicomAttributeRuleId, dicomAttributeRule.attribute, regexValue));
        }
    }, [dispatch, dicomAttributeRule, dicomAttributeRuleId, isRegexModeOn]);

    const handleUndo = useCallback(() => {
        setUndoState({ isModalOpen: true, scope: ModelSelectionScope.DicomAttributeRule, id: dicomAttributeRuleId });
    }, [dicomAttributeRuleId, setUndoState]);

    const handleSelectPreset = useCallback((dicomAttributeName: string) => {
        dispatchDicomAttributeNameChange(dicomAttributeName);
        setIsPresetToggleOpen(false);
    }, [dispatchDicomAttributeNameChange]);

    // don't render anything if we don't have a valid object
    if (dicomAttributeRule === undefined) {
        return null;
    }

    const dicomAttributePresets = [
        <DropdownItem key="title" isPlainText>{t('selectionPage.dicomAttributePresetsLabel')}</DropdownItem>,
        <DropdownItem key="divider" isPlainText><Divider /></DropdownItem>,
        <DropdownItem key="BodyPartExamined" onClick={() => handleSelectPreset('BodyPartExamined')}>BodyPartExamined</DropdownItem>,
        <DropdownItem key="ImageComments" onClick={() => handleSelectPreset('ImageComments')}>ImageComments</DropdownItem>,
        <DropdownItem key="Laterality" onClick={() => handleSelectPreset('Laterality')}>Laterality</DropdownItem>,
        <DropdownItem key="PatientSex" onClick={() => handleSelectPreset('PatientSex')}>PatientSex</DropdownItem>,
        <DropdownItem key="ProtocolName" onClick={() => handleSelectPreset('ProtocolName')}>ProtocolName</DropdownItem>,
        <DropdownItem key="ScanOptions" onClick={() => handleSelectPreset('ScanOptions')}>ScanOptions</DropdownItem>,
        <DropdownItem key="SeriesDescription" onClick={() => handleSelectPreset('SeriesDescription')}>SeriesDescription</DropdownItem>,
        <DropdownItem key="StationName" onClick={() => handleSelectPreset('StationName')}>StationName</DropdownItem>,
        <DropdownItem key="StudyDescription" onClick={() => handleSelectPreset('StudyDescription')}>StudyDescription</DropdownItem>,
    ];


    // TODO: this can be used to display which action menu item (attribute or entire rule) is being targeted by an action but
    // it will require some extra ui work to clean the hover style in weird cases (like pressing esc when the action menu is
    // open, or if a modal is opened). Similar approach can be used to style the entire rule.
    const hoveredClass = '';// isAttributeHovered ? 'attribute-hovered' : '';

    const validated = dicomAttributeRule.isValid ? 'default' : 'error';

    const dropdownItems = [
        <DropdownItem data-test='trigger-rule-dropdown' data-testid='remove-condition' key="removeAttribute" /*onMouseEnter={handleRemoveAttributeHover} onMouseLeave={handleRemoveAttributeHoverLeave}*/ onClick={handleRemoveAttributeButtonClick}>{t('selectionPage.removeDicomAttributeRule')}</DropdownItem>,
        <DropdownItem data-test='trigger-rule-dropdown' data-testid='remove-rule' key="removeDicomRule" onClick={onRemoveDicomRule}>{t('selectionPage.removeDicomRule')}</DropdownItem>
    ];
    if (!disableUndo) {
        dropdownItems.unshift(<DropdownItem key="undoDicomAttributeRuleChanges" isDisabled={!dicomAttributeRule.isModified || dicomAttributeRule.isNew}
            onClick={handleUndo}>{t('selectionPage.undoDicomAttributeRuleChanges')}</DropdownItem>);
    }

    return (
        <>
            <StackItem className={`rule-item dicom-rule-item ${hoveredClass}`}>
                <Split className="dicom-attribute-row" hasGutter>
                    <SplitItem className="dicom-attribute-column">
                        <FormGroup
                            label={t('selectionPage.dicomAttributeRuleNumber', { number: displayId })}
                            fieldId={`attribute-name-field-for-${dicomAttributeRule.id}`}
                            title="A DICOM attribute that is used in this rule."
                            validated={validated}
                            helperTextInvalid={dicomAttributeRule.validationMessage}
                            helperTextInvalidIcon={<ExclamationCircleIcon />}
                        >
                            <InputGroup>
                                <DebouncedTextInput
                                    dataTest='trigger-rule'
                                    dataTestID={`dicom-attribute-${displayId}`}
                                    fieldId={`attribute-name-field-for-${dicomAttributeRule.id}`}
                                    onChange={dispatchDicomAttributeNameChange}
                                    className="input-field"
                                    defaultValue={dicomAttributeRule.attribute}
                                    validated={validated}
                                />
                                <Dropdown
                                    dropdownItems={dicomAttributePresets}
                                    isOpen={isPresetToggleOpen}
                                    title="Optional preset attribute names"
                                    toggle={<DropdownToggle toggleIndicator={null} onToggle={() => setIsPresetToggleOpen(!isPresetToggleOpen)}><CogIcon /></DropdownToggle>}
                                />
                            </InputGroup>
                        </FormGroup>
                    </SplitItem>
                    <SplitItem isFilled>
                        <Stack className={isRegexModeOn ? 'regex-input' : ''}>
                            <StackItem>
                                <FormGroup
                                    label={t('selectionPage.requiredValue')}
                                    fieldId={`attribute-value-field-for-${dicomAttributeRule.id}`}
                                    title="Value (or value range) used in this rule condition."
                                // validated={modelValidation.GetNameValidationState(structureCustomization.name)}
                                // helperTextInvalid={modelValidation.GetNameValidationText(structureCustomization.name)}
                                // helperTextInvalidIcon={<ExclamationCircleIcon />}
                                >
                                    <InputGroup>
                                        <DebouncedTextInput
                                            dataTest='trigger-rule'
                                            dataTestID={`rule-${displayId}`}
                                            fieldId={`attribute-value-field-for-${dicomAttributeRule.id}`}
                                            onChange={dispatchDicomAttributeValueChange}
                                            className={`input-field ${isRegexModeOn ? 'regex-input' : ''}`}
                                            defaultValue={isRegexModeOn ? dicomAttributeRule.value : plainTextValue || ''}
                                        />
                                    </InputGroup>
                                </FormGroup>
                            </StackItem>
                            <StackItem>
                                <Level>
                                    <LevelItem>
                                        <Checkbox
                                            data-test='trigger-rule'
                                            data-testid={`regex-checkbox-${displayId}`}
                                            label={t('selectionPage.enableRegEx')}
                                            isChecked={isRegexModeOn}
                                            onChange={setRegexModeOn}
                                            id={`dicom-attribute-rule-regex-toggle-${dicomAttributeRule.id}`}
                                            isDisabled={plainTextValue === null}
                                        />
                                    </LevelItem>
                                    <LevelItem>
                                        {/* // KNOWN PATTERNFLY BUG: this will result in a deprecation warning in browser console. nothing to really worry about yet though.
                                            // See also: https://github.com/patternfly/patternfly-react/issues/6050 */}
                                        <Popover
                                            headerContent={<div>Advanced DICOM rule edit mode</div>}
                                            bodyContent={<div>Enables the use of <a target="_blank" rel="noreferrer" href="https://docs.python.org/3/howto/regex.html#regex-howto">Regular Expressions</a> in DICOM attribute rule conditions.</div>}
                                        >
                                            <Button variant="plain"><OutlinedQuestionCircleIcon /></Button>
                                        </Popover>
                                    </LevelItem>
                                </Level>
                            </StackItem>
                        </Stack>
                    </SplitItem>
                    <SplitItem>
                        <Dropdown
                            data-test='trigger-rule-dropdown'
                            data-testid={`actions-dropdown-${displayId}`}
                            toggle={<KebabToggle onToggle={() => setActionListOpenState(!isActionListOpen)} />}
                            isPlain
                            position={DropdownPosition.right}
                            dropdownItems={dropdownItems}
                            isOpen={isActionListOpen}
                        />
                    </SplitItem>
                </Split>
                {/* <Divider className="dicom-rule-divider" /> */}
            </StackItem>
            <Modal
                variant={ModalVariant.small}
                title={t('selectionPage.removeDicomAttributeRule.confirm')}
                isOpen={isRemoveAttributeModalOpen}
                onClose={() => setRemoveAttributeModalOpenState(false)}
                actions={[
                    <Button data-test='rule-modal' data-testid='remove' key="confirmRemoval" variant="danger" onClick={handleRemoveAttributeConfirmation}>{t('selectionPage.removeDicomAttributeRule.short')}</Button>,
                    <Button data-test='rule-modal' data-testid='cancel' key="cancel" variant="link" onClick={() => setRemoveAttributeModalOpenState(false)}>{t('common.cancel')}</Button>
                ]}
            >
                Do you want to remove this DICOM attribute/value condition "{dicomAttributeRule.attribute}: {dicomAttributeRule.value}" from this model selection rule? Note that your changes won't be saved until you click on "Save".
            </Modal>
        </>
    );
}

export default ModelSelectionRuleDicomAttribute;
